import { useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { Volume2 } from 'lucide-react';
import { PartyUiContext } from './context';
import { readAloud, type ReadAloudConfig } from './speech';

export function useReadAloudConfig(): ReadAloudConfig {
  return useSyncExternalStore(readAloud.subscribe, readAloud.getSnapshot, readAloud.getSnapshot);
}

/**
 * Reads `parts` aloud once each time `key` changes (a new question, the reveal), on phones with read aloud on.
 * Never on the TV / audience screen. Speech stops when the key changes or the screen goes away.
 * Returns whether the mode is on and `again()` to read it once more (a tap, so it also unlocks iOS).
 */
export function useReadAloud(parts: string[] | null, key: string | null): { on: boolean; again: () => void } {
  const ui = useContext(PartyUiContext);
  const cfg = useReadAloudConfig();
  const on = cfg.enabled && readAloud.supported && !ui?.display;
  const latest = useRef(parts);
  latest.current = parts;
  useEffect(() => {
    if (!on || !key || !latest.current?.length) return;
    readAloud.speak(latest.current);
    return () => readAloud.cancel();
  }, [on, key]);
  const again = useCallback(() => {
    if (latest.current?.length) readAloud.speak(latest.current);
  }, []);
  return { on, again };
}

/** Small "Read again" button, shown only while read aloud is on. */
export function ReadAgainButton({ onClick, className = '' }: { onClick: () => void; className?: string }) {
  return (
    <button type="button" className={`btn btn-ghost btn-sm justify-self-center self-center ${className}`} onClick={onClick} data-testid="read-again">
      <Volume2 size={16} aria-hidden /> Read again
    </button>
  );
}
